export interface CohortPreset {
  id: string;
  code: string;
  name: string;
  sponsor: string;
  description: string;
  defaultCurrency: string;
  badge: string;
}

export interface CohortAccessSettings {
  isCohortMember: boolean;
  cohortId: string;
  cohortName: string;
  cohortCode: string;
  sponsorName: string;
  activatedAt?: string;
  sharingEnabled: boolean; // Default true for sponsored cohort seat
  shareRunwayBands: boolean; // Default true (Traffic light: Green/Yellow/Red & Runway months)
  shareTaxComplianceStatus: boolean; // Default true (SARS/IRS/HMRC tax reserve readiness)
  deepAuditEnabled: boolean; // Default false (Founder-controlled read-only pass)
  deepAuditPassToken?: string;
  deepAuditGrantedAt?: string;
  deepAuditExpiryDays?: number;
  lastTelemetrySync?: string;
}

export const KNOWN_COHORT_PRESETS: CohortPreset[] = [
  {
    id: 'pilot_alpha',
    code: 'PILOT-ALPHA',
    name: 'Tier-1 Seed Accelerator (Cohort Alpha)',
    sponsor: 'Venture Acceleration Partner',
    description: 'Seed-stage high-velocity founder growth & runway optimization track',
    defaultCurrency: 'USD',
    badge: '🚀 Seed Cohort Alpha'
  },
  {
    id: 'venture_sprint',
    code: 'VENTURE-SPRINT',
    name: 'Regional Venture Studio (Sprint Track)',
    sponsor: 'Regional Studio Partner',
    description: 'B2B enterprise acceleration & market validation track',
    defaultCurrency: 'USD',
    badge: '⚡ Venture Sprint'
  },
  {
    id: 'global_incubator',
    code: 'GLOBAL-INCUBATOR',
    name: 'Global Tech Incubator (Residency Batch)',
    sponsor: 'Ecosystem Innovation Hub',
    description: 'Day-zero sovereign founder residency & unit economics calibration',
    defaultCurrency: 'USD',
    badge: '🌐 Global Residency'
  },
  {
    id: 'enterprise_lab',
    code: 'ENTERPRISE-LAB',
    name: 'Corporate Innovation Lab (Growth Pilot)',
    sponsor: 'Enterprise Innovation Desk',
    description: 'Corporate pilot deployment & institutional compliance track',
    defaultCurrency: 'USD',
    badge: '🏢 Enterprise Lab'
  }
];

export const DEFAULT_COHORT_SETTINGS: CohortAccessSettings = {
  isCohortMember: false,
  cohortId: '',
  cohortName: '',
  cohortCode: '',
  sponsorName: '',
  sharingEnabled: false,
  shareRunwayBands: true,
  shareTaxComplianceStatus: true,
  deepAuditEnabled: false
};

const STORAGE_KEY = 'solostack_cohort_settings';

export function getCohortSettings(): CohortAccessSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      
      // Automatic sanitize migration: replace old mock trademarks with generic archetypes
      const name = (parsed.cohortName || '').toLowerCase();
      const sponsor = (parsed.sponsorName || '').toLowerCase();
      const code = (parsed.cohortCode || '').toUpperCase();

      if (code === 'YC26' || name.includes('y combinator') || sponsor.includes('y combinator') || code === 'ALTALAB2026' || name.includes('altalab')) {
        parsed.cohortCode = 'PILOT-ALPHA';
        parsed.cohortName = 'Tier-1 Seed Accelerator (Cohort Alpha)';
        parsed.sponsorName = 'Venture Acceleration Partner';
        parsed.cohortId = 'pilot_alpha';
        saveCohortSettings(parsed);
      } else if (code === 'SPEEDRUN' || name.includes('speedrun') || name.includes('a16z') || code === 'GRINDSTONE' || name.includes('grindstone')) {
        parsed.cohortCode = 'VENTURE-SPRINT';
        parsed.cohortName = 'Regional Venture Studio (Sprint Track)';
        parsed.sponsorName = 'Regional Studio Partner';
        parsed.cohortId = 'venture_sprint';
        saveCohortSettings(parsed);
      } else if (code === 'ANTLER' || name.includes('antler')) {
        parsed.cohortCode = 'GLOBAL-INCUBATOR';
        parsed.cohortName = 'Global Tech Incubator (Residency Batch)';
        parsed.sponsorName = 'Ecosystem Innovation Hub';
        parsed.cohortId = 'global_incubator';
        saveCohortSettings(parsed);
      }

      return { ...DEFAULT_COHORT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.error('Failed to load cohort settings from localStorage:', e);
  }
  return DEFAULT_COHORT_SETTINGS;
}

export function saveCohortSettings(settings: CohortAccessSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent('cohortSettingsUpdated', { detail: settings }));
  } catch (e) {
    console.error('Failed to save cohort settings:', e);
  }
}

export function activateCohortByCode(code: string): { success: boolean; cohort?: CohortPreset; settings: CohortAccessSettings; message: string } {
  const normalized = code.trim().toUpperCase();
  const preset = KNOWN_COHORT_PRESETS.find(p => p.code === normalized || p.id.toUpperCase() === normalized);

  if (preset) {
    const newSettings: CohortAccessSettings = {
      isCohortMember: true,
      cohortId: preset.id,
      cohortName: preset.name,
      cohortCode: preset.code,
      sponsorName: preset.sponsor,
      activatedAt: new Date().toISOString(),
      sharingEnabled: true, // Default active on sponsored cohort seat
      shareRunwayBands: true,
      shareTaxComplianceStatus: true,
      deepAuditEnabled: false,
      lastTelemetrySync: new Date().toISOString()
    };
    saveCohortSettings(newSettings);
    return {
      success: true,
      cohort: preset,
      settings: newSettings,
      message: `🎉 Successfully activated ${preset.name} sponsorship!`
    };
  }

  // Custom cohort code fallback
  if (normalized.length >= 3) {
    const customPreset: CohortPreset = {
      id: `custom_${normalized.toLowerCase()}`,
      code: normalized,
      name: `${normalized} Venture Cohort`,
      sponsor: 'Designated Accelerator Partner',
      description: 'Institutional Cohort Program',
      defaultCurrency: 'USD',
      badge: `🤝 ${normalized} Cohort`
    };
    const newSettings: CohortAccessSettings = {
      isCohortMember: true,
      cohortId: customPreset.id,
      cohortName: customPreset.name,
      cohortCode: customPreset.code,
      sponsorName: customPreset.sponsor,
      activatedAt: new Date().toISOString(),
      sharingEnabled: true,
      shareRunwayBands: true,
      shareTaxComplianceStatus: true,
      deepAuditEnabled: false,
      lastTelemetrySync: new Date().toISOString()
    };
    saveCohortSettings(newSettings);
    return {
      success: true,
      cohort: customPreset,
      settings: newSettings,
      message: `🎉 Successfully activated ${normalized} Cohort license!`
    };
  }

  return {
    success: false,
    settings: getCohortSettings(),
    message: 'Invalid cohort code. Please check your invitation voucher.'
  };
}

export function grantDeepAuditPass(days: number = 30): CohortAccessSettings {
  const current = getCohortSettings();
  const token = `AUDIT-PASS-${Math.random().toString(36).substring(2, 9).toUpperCase()}-${Date.now().toString().slice(-4)}`;
  const updated: CohortAccessSettings = {
    ...current,
    deepAuditEnabled: true,
    deepAuditPassToken: token,
    deepAuditGrantedAt: new Date().toISOString(),
    deepAuditExpiryDays: days
  };
  saveCohortSettings(updated);
  return updated;
}

export function revokeDeepAuditPass(): CohortAccessSettings {
  const current = getCohortSettings();
  const updated: CohortAccessSettings = {
    ...current,
    deepAuditEnabled: false,
    deepAuditPassToken: undefined,
    deepAuditGrantedAt: undefined,
    deepAuditExpiryDays: undefined
  };
  saveCohortSettings(updated);
  return updated;
}

export function leaveCohort(): CohortAccessSettings {
  const reset = { ...DEFAULT_COHORT_SETTINGS };
  saveCohortSettings(reset);
  return reset;
}

export const deactivateCohort = leaveCohort;
